<script setup lang="ts">
import { BigNumber } from "ethers";
import { computed, nextTick, onMounted, type PropType, ref } from "vue";
import { DisplayCurrencyMode } from "~/application/enums/DisplayCurrencyMode.enum";
import useConvert from "~/composables/useConvert";
import usePOLPrice from "~/stores/POLprice";

const props = defineProps({
  value: {
    type: BigNumber,
    default: 0,
  },
  min: {
    type: BigNumber,
    required: false,
    default: 0,
  },
  max: {
    type: BigNumber,
    required: false,
  },
  decimals: {
    type: Number,
    required: false,
    default: 2,
  },
  defaultMode: {
    type: String as PropType<DisplayCurrencyMode>,
    required: false,
    default: DisplayCurrencyMode.MTC,
  },
  canSwitchMode: {
    type: Boolean,
    default: true,
  },
});

const inputAmount = ref<number>();
const mode = ref(DisplayCurrencyMode.MTC);
const { weiToMatic, weiToUsd, maticToWei, usdToMtc, mtcToUsd } = useConvert;
const polPrice = usePOLPrice();

const emits = defineEmits<{
  (event: "weiAmount", value: BigNumber): void;
}>();

const inputRef = ref<HTMLInputElement | null>(null);

onMounted(async () => {
  mode.value = props.defaultMode;

  await nextTick();
  inputRef.value?.focus();

  if (!props.value) return;
  if (mode.value === DisplayCurrencyMode.MTC) {
    inputAmount.value = parseFloat(weiToMatic(props.value).toFixed(props.decimals));
  } else {
    inputAmount.value = parseFloat(weiToUsd(props.value).toFixed(props.decimals));
  }
});

const dollarAmount = computed(() => {
  if (!inputAmount.value) return 0;
  return inputAmount.value / polPrice.rates.usd;
});

function onInput(e) {
  const str = e.target.value.replace(",", ".");

  if (e.target.value === "") {
    inputAmount.value = e.target.value;
    emitChange();
    return;
  }

  let newVal = parseFloat(str);

  if (!isNaN(newVal)) {
    //check min and max values only on change to prevent weird input rewrite
    const bnewval = maticToWei(newVal);

    if (bnewval.lt(props.min)) {
      newVal = weiToMatic(props.min);
    }

    inputAmount.value = newVal;
    emitChange();
  }
}

function emitChange() {
  const wei = mode.value === DisplayCurrencyMode.MTC ? inputAmount.value : dollarAmount.value;
  try {
    emits("weiAmount", maticToWei(wei || 0));
  } catch (error) {
    emits("weiAmount", props.value);
  }
}

function switchMode() {
  mode.value =
    mode.value === DisplayCurrencyMode.MTC ? DisplayCurrencyMode.DOLLAR : DisplayCurrencyMode.MTC;
  if (mode.value === DisplayCurrencyMode.MTC) {
    inputAmount.value = parseFloat(weiToMatic(props.value).toFixed(props.decimals));
  } else {
    inputAmount.value = parseFloat(weiToUsd(props.value).toFixed(props.decimals));
  }
  emits("weiAmount", props.value);
}
</script>

<template>
  <div class="flex flex-col w-full items-start gap-f2">
    <div class="flex flex-row relative items-center justify-end w-full">
      <button
        v-if="canSwitchMode"
        @click="switchMode"
        class="p-2 rounded-full text-black bg-white hover:bg-primary absolute right-2"
      >
        <icon-mdi:swap-horizontal />
      </button>
      <icon-clarity:dollar-solid
        class="inline-icon absolute left-2 text-grey"
        v-if="mode === DisplayCurrencyMode.DOLLAR"
      />
      <icon-fgc-w-matic class="inline-icon absolute left-2" v-else />
      <input
        ref="inputRef"
        class="form-input-fg w-full pl-9"
        type="number"
        :value="inputAmount"
        @input="onInput"
      />
    </div>
    <div class="flex items-center gap-f2">
      <span class="text-grey" v-if="mode === DisplayCurrencyMode.DOLLAR">
        ≈ {{ usdToMtc(inputAmount || 0) }} MATIC
      </span>
      <span class="text-grey" v-else> ≈ {{ inputAmount ? mtcToUsd(inputAmount) : 0 }} </span>
      {{ inputAmount }}
      <slot name="msg" />
    </div>
  </div>
</template>
